import React, { useState, useEffect } from 'react';
import API_BASE from '../config';
import ThankYou from './ThankYou';

function RegistrationForm() {
  const [step, setStep] = useState(1);
  const [student, setStudent] = useState({ firstName: '', lastName: '', class: '' });
  const [parentContact, setParentContact] = useState({ email: '', countryCode: '+39', phone: '' });
  const [participants, setParticipants] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [config, setConfig] = useState({ menus: [], relations: [] });
  const [rulebookAccepted, setRulebookAccepted] = useState(false);
  const [completedRegistration, setCompletedRegistration] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Delegation state
  const [delegationActive, setDelegationActive] = useState(false);
  const [delegation, setDelegation] = useState({
    delegatedStudent: { firstName: '', lastName: '', class: '' },
    delegatedParent: { firstName: '', lastName: '' }
  });
  const [delegationError, setDelegationError] = useState('');
  const [delegationWarning, setDelegationWarning] = useState('');
  const [suggestion, setSuggestion] = useState(null);

  // Liberatoria state
  const [liberatoria, setLiberatoria] = useState({
    parent1: { firstName: '', lastName: '', birthPlace: '', birthProvince: '', birthDate: '', address: '', city: '', province: '' },
    parent2: { firstName: '', lastName: '', birthPlace: '', birthProvince: '', birthDate: '', address: '', city: '', province: '', sameAddress: false }
  });

  // E-Sign state
  const [liberatoriaMode, setLiberatoriaMode] = useState('paper'); // 'paper' | 'electronic'
  const [delegationMode, setDelegationMode] = useState('paper'); // 'paper' | 'electronic'
  const [otpEmail, setOtpEmail] = useState(''); // email for OTP (pre-filled from parentContact)
  const [delegatedParentEmail, setDelegatedParentEmail] = useState(''); // email for delegated parent OTP
  const [otpKey, setOtpKey] = useState(''); // key returned by send-otp
  const [otpCode, setOtpCode] = useState('');
  const [otpCountdown, setOtpCountdown] = useState(600);
  const [otpResendCooldown, setOtpResendCooldown] = useState(0);
  const [otpMaskedEmail, setOtpMaskedEmail] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState('');

  // Auto-format date input as DD/MM/YYYY
  const formatDateInput = (value, prevValue) => {
    // Only allow digits and slashes
    let digits = value.replace(/[^\d]/g, '');
    if (digits.length > 8) digits = digits.substring(0, 8);
    let formatted = '';
    if (digits.length <= 2) {
      formatted = digits;
      if (digits.length === 2) formatted += '/';
    } else if (digits.length <= 4) {
      formatted = digits.substring(0, 2) + '/' + digits.substring(2);
      if (digits.length === 4) formatted += '/';
    } else {
      formatted = digits.substring(0, 2) + '/' + digits.substring(2, 4) + '/' + digits.substring(4, 8);
    }
    return formatted;
  };

  const countryCodes = [
    { code: '+39', country: 'Italia' },
    { code: '+1', country: 'USA/Canada' },
    { code: '+33', country: 'Francia' },
    { code: '+49', country: 'Germania' },
    { code: '+44', country: 'UK' },
    { code: '+34', country: 'Spagna' },
    { code: '+41', country: 'Svizzera' },
    { code: '+43', country: 'Austria' },
    { code: '+32', country: 'Belgio' },
    { code: '+31', country: 'Paesi Bassi' }
  ];

  useEffect(() => { fetchConfig(); }, []);

  // OTP countdown timer
  useEffect(() => {
    if (step === 4 && otpCountdown > 0) {
      const timer = setInterval(() => setOtpCountdown(c => c - 1), 1000);
      return () => clearInterval(timer);
    }
  }, [step, otpCountdown]);

  // OTP resend cooldown
  useEffect(() => {
    if (otpResendCooldown > 0) {
      const timer = setInterval(() => setOtpResendCooldown(c => c - 1), 1000);
      return () => clearInterval(timer);
    }
  }, [otpResendCooldown]);

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API_BASE}/config`);
      const data = await response.json();
      setConfig(data);
    } catch (err) { console.error('Failed to load config:', err); }
  };

  const isDeadlinePassed = () => {
    if (!config.registrationDeadline) return false;
    return new Date() > new Date(config.registrationDeadline);
  };

  const formatDeadline = () => {
    if (!config.registrationDeadline) return null;
    const d = config.registrationDeadline.substring(0, 10).split('-');
    return `${d[2]}/${d[1]}/${d[0]}`;
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) => /^\d{6,15}$/.test(phone);

  const validateStudent = async () => {
    setError('');
    setSuccess('');
    setSuggestion(null);

    if (!student.firstName.trim() || !student.lastName.trim() || !student.class) {
      setError('Compilare nome, cognome e selezionare la classe.');
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/validate-student`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstName: student.firstName.trim(), lastName: student.lastName.trim(), class: student.class })
      });
      if (response.ok) {
        let data;
        try { data = await response.json(); } catch (e) { setError('Errore nella verifica dello studente. Riprovare.'); return; }
        if (data.valid === true) {
          setParticipants([{
            firstName: student.firstName,
            lastName: student.lastName,
            relation: 'Studente',
            menu: config.menus[0]?.name || 'Hamburger'
          }]);
          setStep(2);
        } else {
          setError('Errore nella verifica dello studente. Riprovare.');
        }
      } else if (response.status === 409) {
        const data = await response.json();
        if (data.siblingOf) {
          setError(`ℹ️ ${student.firstName} ${student.lastName} è già registrato come fratello/sorella nella registrazione di ${data.siblingOf}. Un link per modificare la registrazione è stato inviato a ${data.maskedEmail}. In caso di problemi, contattare il rappresentante di classe.`);
        } else {
          setError(`ℹ️ Esiste già una registrazione per ${student.firstName} ${student.lastName}. Un link per visualizzare e modificare la registrazione è stato inviato a ${data.maskedEmail}. In caso di problemi, contattare il rappresentante di classe.`);
        }
      } else if (response.status === 404) {
        const data = await response.json();
        if (data.suggestion) {
          setError(data.error);
          setSuggestion(data.suggestion);
        } else {
          setError(data.error || 'Studente non trovato. Controllare il nome, cognome e classe.');
          setSuggestion(null);
        }
      } else {
        setError('Errore nella verifica dello studente. Riprovare.');
      }
    } catch (err) {
      setError('Impossibile connettersi al server. Assicurarsi che il backend sia in esecuzione.');
    }
  };

  const addParticipant = () => {
    const genitoreExists = participants.some(p => p.relation === 'Genitore');
    const defaultRelation = genitoreExists ? (config.relations.find(r => r === 'Altro Genitore') || config.relations[1] || 'Genitore') : (config.relations[1] || 'Genitore');
    setParticipants([...participants, {
      firstName: '', lastName: '', relation: defaultRelation, menu: config.menus[0]?.name || 'Hamburger',
      siblingStudent: false, siblingClass: ''
    }]);
  };

  const updateParticipant = (index, field, value) => {
    const updated = [...participants];
    updated[index][field] = value;
    // Reset sibling fields when relation changes away from Sibling
    if (field === 'relation' && value !== 'Fratello/Sorella') {
      updated[index].siblingStudent = false;
      updated[index].siblingClass = '';
    }
    setParticipants(updated);
  };

  const removeParticipant = (index) => {
    setParticipants(participants.filter((_, i) => i !== index));
  };

  const validateDelegatedStudent = async () => {
    setDelegationError('');
    setDelegationWarning('');
    const ds = delegation.delegatedStudent;
    if (!ds.firstName.trim() || !ds.lastName.trim() || !ds.class) {
      setDelegationError('Compilare tutti i campi dello studente delegato.');
      return false;
    }
    // Cannot delegate to self
    if (ds.firstName.toLowerCase() === student.firstName.toLowerCase() &&
        ds.lastName.toLowerCase() === student.lastName.toLowerCase() &&
        ds.class === student.class) {
      setDelegationError('Lo studente delegato non può essere lo stesso studente che si sta registrando.');
      return false;
    }
    try {
      // Validate delegated student with fuzzy matching
      const sibRes = await fetch(`${API_BASE}/validate-student-exists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstName: ds.firstName.trim(), lastName: ds.lastName.trim(), siblingClass: ds.class })
      });
      const sibData = await sibRes.json();
      if (!sibData.valid) {
        if (sibData.suggestion) {
          setDelegationError(`${sibData.error} Suggerimento: ${sibData.suggestion.firstName} ${sibData.suggestion.lastName} (Classe ${sibData.suggestion.class}).`);
        } else {
          setDelegationError(sibData.error || 'Studente delegato non trovato. Controllare il nome e la classe.');
        }
        return false;
      }

      // Check if delegated student has a registration (meaning the parent registered)
      // We use the validate-student endpoint which returns 409 if registered
      const regCheckRes = await fetch(`${API_BASE}/validate-student`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstName: ds.firstName, lastName: ds.lastName, class: ds.class })
      });
      if (regCheckRes.ok) {
        // 200 means no registration exists for that student
        setDelegationWarning(`⚠️ Attenzione: ${ds.firstName} ${ds.lastName} non ha ancora una registrazione. Il genitore delegato non potrà accedere alla festa senza una registrazione.`);
      }
      // 409 means registration exists — that's good, no warning needed
    } catch (err) {
      setDelegationError('Errore nella verifica dello studente delegato.');
      return false;
    }
    return true;
  };

  const goToStep3 = () => {
    // Validate step 2 fields before advancing
    if (!validateEmail(parentContact.email)) { setError('Inserire un indirizzo email valido.'); return; }
    if (!validatePhone(parentContact.phone)) { setError('Inserire un numero di telefono valido.'); return; }
    if (participants.some(p => !p.firstName.trim() || !p.lastName.trim())) { setError('Compilare nome e cognome di tutti i partecipanti.'); return; }
    if (!delegationActive) {
      const hasParent = participants.some(p => p.relation === 'Genitore' || p.relation === 'Altro Genitore');
      if (!hasParent) { setError('Almeno un genitore deve accompagnare il bambino, oppure abilitare la delega.'); return; }
    }

    // Pre-fill liberatoria from participants (only if not already filled)
    const parent1Participant = participants.find(p => p.relation === 'Genitore');
    const parent2Participant = participants.find(p => p.relation === 'Altro Genitore');

    const newLib = { ...liberatoria };
    if (parent1Participant && !newLib.parent1.firstName && !newLib.parent1.lastName) {
      newLib.parent1 = { ...newLib.parent1, firstName: parent1Participant.firstName, lastName: parent1Participant.lastName };
    }
    if (parent2Participant && !newLib.parent2.firstName && !newLib.parent2.lastName) {
      newLib.parent2 = { ...newLib.parent2, firstName: parent2Participant.firstName, lastName: parent2Participant.lastName };
    }
    setLiberatoria(newLib);

    // Pre-fill OTP email from parent contact
    if (!otpEmail) setOtpEmail(parentContact.email);
    // Pre-fill delegated parent email if delegation active
    if (delegationActive && !delegatedParentEmail) setDelegatedParentEmail('');

    setError('');
    setStep(3);
  };

  const needsOtp = () => liberatoriaMode === 'electronic' || (delegationActive && delegationMode === 'electronic');

  const validateStep3 = () => {
    if (!rulebookAccepted) { setError('È necessario accettare il regolamento prima di procedere.'); return false; }

    // Validate liberatoria fields if electronic
    if (liberatoriaMode === 'electronic') {
      const p1 = liberatoria.parent1;
      if (!p1.firstName.trim() || !p1.lastName.trim() || !p1.birthPlace.trim() || !p1.birthProvince.trim() || !p1.birthDate.trim() || !p1.address.trim() || !p1.city.trim() || !p1.province.trim()) {
        setError('Per la firma elettronica, tutti i campi del primo genitore sono obbligatori.');
        return false;
      }
      // Validate parent2 if name filled
      const p2 = liberatoria.parent2;
      if (p2.firstName.trim() || p2.lastName.trim()) {
        if (!p2.firstName.trim() || !p2.lastName.trim() || !p2.birthPlace.trim() || !p2.birthProvince.trim() || !p2.birthDate.trim()) {
          setError('Se compilato il nome del secondo genitore, tutti i campi sono obbligatori per la firma elettronica.');
          return false;
        }
        if (!p2.sameAddress && (!p2.address.trim() || !p2.city.trim() || !p2.province.trim())) {
          setError('Indirizzo del secondo genitore obbligatorio (o selezionare "Stesso indirizzo").');
          return false;
        }
      }
      if (!otpEmail || !validateEmail(otpEmail)) {
        setError('Inserire un indirizzo email valido per ricevere il codice OTP.');
        return false;
      }
    }

    // Validate delegation email if electronic
    if (delegationActive && delegationMode === 'electronic') {
      if (!delegatedParentEmail || !validateEmail(delegatedParentEmail)) {
        setError('Inserire un indirizzo email valido per il genitore delegato.');
        return false;
      }
    }

    return true;
  };

  const handleStep3Submit = async () => {
    if (submitting) return;
    setError('');

    if (!validateStep3()) return;

    // Delegation validation
    if (delegationActive) {
      const dp = delegation.delegatedParent;
      if (!dp.firstName.trim() || !dp.lastName.trim()) {
        setError('Compilare il nome del genitore delegato.');
        return;
      }
      const delegatedStudentValid = await validateDelegatedStudent();
      if (!delegatedStudentValid) return;
    }

    // Validate sibling students
    for (const p of participants) {
      if (p.siblingStudent && p.relation === 'Fratello/Sorella') {
        if (!p.siblingClass) { setError(`Selezionare una classe per il fratello/sorella ${p.firstName} ${p.lastName}.`); return; }
        try {
          const res = await fetch(`${API_BASE}/validate-student-exists`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ firstName: p.firstName.trim(), lastName: p.lastName.trim(), siblingClass: p.siblingClass })
          });
          const data = await res.json();
          if (!data.valid) {
            if (data.suggestion) {
              setError(`${data.error} Suggerimento: ${data.suggestion.firstName} ${data.suggestion.lastName} (Classe ${data.suggestion.class}).`);
            } else {
              setError(data.error);
            }
            return;
          }
        } catch (err) { setError('Errore nella verifica del fratello/sorella.'); return; }
      }
    }

    if (needsOtp()) {
      // Send OTP then go to step 4
      await sendOtp();
    } else {
      // All paper — save directly
      await saveRegistration();
    }
  };

  const buildRegistrationBody = () => {
    const fullPhone = `${parentContact.countryCode}-${parentContact.phone}`;
    return {
      student,
      parentContact: { ...parentContact, phone: fullPhone },
      participants,
      delegation: delegationActive
        ? { active: true, ...delegation, mode: delegationMode, delegatedParent: { ...delegation.delegatedParent, email: delegatedParentEmail || '' } }
        : { active: false },
      liberatoria: {
        ...liberatoria,
        mode: liberatoriaMode
      }
    };
  };

  const sendOtp = async () => {
    setOtpSending(true);
    setOtpError('');
    try {
      const response = await fetch(`${API_BASE}/registrations/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: otpEmail, registrationData: buildRegistrationBody() })
      });
      if (response.ok) {
        const data = await response.json();
        setOtpKey(data.otpKey);
        setOtpMaskedEmail(data.maskedEmail);
        setOtpCountdown(600);
        setOtpResendCooldown(60);
        setOtpCode('');
        setStep(4);
      } else {
        const data = await response.json();
        setError(data.error || 'Errore nell\'invio del codice OTP.');
      }
    } catch (err) { setError('Errore di connessione. Riprovare.'); }
    setOtpSending(false);
  };

  const resendOtp = async () => {
    setOtpSending(true);
    setOtpError('');
    try {
      const response = await fetch(`${API_BASE}/registrations/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: otpEmail, registrationData: buildRegistrationBody() })
      });
      if (response.ok) {
        const data = await response.json();
        setOtpKey(data.otpKey);
        setOtpMaskedEmail(data.maskedEmail);
        setOtpCountdown(600);
        setOtpResendCooldown(60);
        setOtpCode('');
        setOtpError('');
      } else {
        const data = await response.json();
        setOtpError(data.error || 'Errore nel reinvio del codice.');
      }
    } catch (err) { setOtpError('Errore di connessione.'); }
    setOtpSending(false);
  };

  const verifyOtp = async () => {
    setOtpVerifying(true);
    setOtpError('');
    try {
      const response = await fetch(`${API_BASE}/registrations/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ otpKey, code: otpCode })
      });
      if (response.ok) {
        const data = await response.json();
        setCompletedRegistration(data.registration);
      } else {
        const data = await response.json();
        setOtpError(data.error || 'Codice errato o scaduto.');
      }
    } catch (err) { setOtpError('Errore di connessione.'); }
    setOtpVerifying(false);
  };

  const changeOtpEmail = async (newEmail) => {
    if (!validateEmail(newEmail)) { setOtpError('Inserire un indirizzo email valido.'); return; }
    setOtpEmail(newEmail);
    // Send new OTP to new email
    setOtpSending(true);
    setOtpError('');
    try {
      const response = await fetch(`${API_BASE}/registrations/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newEmail, registrationData: buildRegistrationBody() })
      });
      if (response.ok) {
        const data = await response.json();
        setOtpKey(data.otpKey);
        setOtpMaskedEmail(data.maskedEmail);
        setOtpCountdown(600);
        setOtpResendCooldown(60);
        setOtpCode('');
      } else {
        const data = await response.json();
        setOtpError(data.error || 'Errore nell\'invio del codice.');
      }
    } catch (err) { setOtpError('Errore di connessione.'); }
    setOtpSending(false);
  };

  const saveRegistration = async () => {
    setSubmitting(true);
    try {
      const body = buildRegistrationBody();
      const response = await fetch(`${API_BASE}/registrations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const data = await response.json();
        setCompletedRegistration(data.registration);
      } else {
        const data = await response.json();
        setError(data.error || 'Registrazione fallita. Riprovare.');
      }
    } catch (err) { setError('Errore nell\'invio della registrazione. Riprovare.'); }
    setSubmitting(false);
  };

  const formatOtpTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  const resetForm = () => {
    setStep(1);
    setStudent({ firstName: '', lastName: '', class: '' });
    setParentContact({ email: '', countryCode: '+39', phone: '' });
    setParticipants([]);
    setError('');
    setSuccess('');
    setRulebookAccepted(false);
    setDelegationActive(false);
    setDelegation({ delegatedStudent: { firstName: '', lastName: '', class: '' }, delegatedParent: { firstName: '', lastName: '' } });
    setLiberatoria({ parent1: { firstName: '', lastName: '', birthPlace: '', birthProvince: '', birthDate: '', address: '', city: '', province: '' }, parent2: { firstName: '', lastName: '', birthPlace: '', birthProvince: '', birthDate: '', address: '', city: '', province: '', sameAddress: false } });
    setLiberatoriaMode('paper');
    setDelegationMode('paper');
    setOtpEmail('');
    setDelegatedParentEmail('');
    setOtpKey('');
    setOtpCode('');
    setOtpCountdown(600);
    setOtpResendCooldown(0);
    setOtpMaskedEmail('');
    setOtpError('');
    setCompletedRegistration(null);
  };

  if (completedRegistration) {
    return <ThankYou registration={completedRegistration} onBack={resetForm} />;
  }

  return (
    <div>
      {step === 1 && (
        <div>
          <h2>Passo 1: Informazioni Studente</h2>
          {isDeadlinePassed() ? (
            <div className="warning-box">
              ⚠️ Le registrazioni sono chiuse dal {formatDeadline()}. Non è più possibile effettuare nuove registrazioni o modifiche.
            </div>
          ) : (
            config.registrationDeadline && (
              <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
                📅 Scadenza registrazioni: <strong>{formatDeadline()}</strong>
              </p>
            )
          )}
          <div className="form-group">
            <label>Nome</label>
            <input type="text" value={student.firstName}
              onChange={(e) => setStudent({ ...student, firstName: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Cognome</label>
            <input type="text" value={student.lastName}
              onChange={(e) => setStudent({ ...student, lastName: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Classe</label>
            <select value={student.class} onChange={(e) => setStudent({ ...student, class: e.target.value })}>
              <option value="">Seleziona classe</option>
              {(config.classes || ['1A','1B','1C','2A','2B','2C','3A','3B','3C']).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {error && <div className="error">{error}</div>}
          {suggestion && (
            <div className="suggestion-box">
              <button className="btn-primary" onClick={() => {
                setStudent({ firstName: suggestion.firstName, lastName: suggestion.lastName, class: suggestion.class });
                setSuggestion(null);
                setError('');
              }}>
                Usa: {suggestion.firstName} {suggestion.lastName} ({suggestion.class})
              </button>
            </div>
          )}
          {success && <div className="success">{success}</div>}
          <button className="btn-primary" onClick={validateStudent} disabled={isDeadlinePassed()}>Verifica Studente</button>
        </div>
      )}

      {step === 2 && (
        <div>
          <h2>Passo 2: Contatti e Partecipanti</h2>

          <div className="form-group">
            <label>Email del Genitore *</label>
            <input type="email" value={parentContact.email} placeholder="esempio@email.com"
              onChange={(e) => setParentContact({ ...parentContact, email: e.target.value })} />
          </div>

          <div className="form-group">
            <label>Telefono del Genitore *</label>
            <div className="phone-input-group">
              <select value={parentContact.countryCode} className="country-code-select"
                onChange={(e) => setParentContact({ ...parentContact, countryCode: e.target.value })}>
                {countryCodes.map(item => (
                  <option key={item.code} value={item.code}>{item.code} ({item.country})</option>
                ))}
              </select>
              <input type="tel" value={parentContact.phone} placeholder="1234567890" className="phone-number-input"
                onChange={(e) => setParentContact({ ...parentContact, phone: e.target.value.replace(/\D/g, '') })} />
            </div>
          </div>

          {/* Delegation Section */}
          <div className="delegation-section">
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input type="checkbox" checked={delegationActive}
                  onChange={(e) => {
                    const active = e.target.checked;
                    setDelegationActive(active);
                    setDelegationWarning('');
                    setDelegationError('');
                    if (active) {
                      // Keep only the student (first participant)
                      setParticipants(prev => [prev[0]]);
                    }
                  }} />
                Mio figlio sarà accompagnato dal genitore di un altro studente (delega)
              </label>
            </div>

            {delegationActive && (
              <div className="delegation-form">
                <h4>Studente Delegato</h4>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '10px' }}>
                  Lo studente il cui genitore accompagnerà vostro figlio.
                </p>
                <div className="form-row">
                  <div className="form-group">
                    <label>Nome</label>
                    <input type="text" value={delegation.delegatedStudent.firstName}
                      onChange={(e) => setDelegation({
                        ...delegation,
                        delegatedStudent: { ...delegation.delegatedStudent, firstName: e.target.value }
                      })} />
                  </div>
                  <div className="form-group">
                    <label>Cognome</label>
                    <input type="text" value={delegation.delegatedStudent.lastName}
                      onChange={(e) => setDelegation({
                        ...delegation,
                        delegatedStudent: { ...delegation.delegatedStudent, lastName: e.target.value }
                      })} />
                  </div>
                  <div className="form-group">
                    <label>Classe</label>
                    <select value={delegation.delegatedStudent.class}
                      onChange={(e) => setDelegation({
                        ...delegation,
                        delegatedStudent: { ...delegation.delegatedStudent, class: e.target.value }
                      })}>
                      <option value="">Seleziona classe</option>
                      {(config.classes || ['1A','1B','1C','2A','2B','2C','3A','3B','3C']).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>

                <h4>Genitore Delegato</h4>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '10px' }}>
                  Il genitore che accompagnerà vostro figlio.
                </p>
                <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                  <div className="form-group">
                    <label>Nome</label>
                    <input type="text" value={delegation.delegatedParent.firstName}
                      onChange={(e) => setDelegation({
                        ...delegation,
                        delegatedParent: { ...delegation.delegatedParent, firstName: e.target.value }
                      })} />
                  </div>
                  <div className="form-group">
                    <label>Cognome</label>
                    <input type="text" value={delegation.delegatedParent.lastName}
                      onChange={(e) => setDelegation({
                        ...delegation,
                        delegatedParent: { ...delegation.delegatedParent, lastName: e.target.value }
                      })} />
                  </div>
                </div>

                {delegationError && <div className="error">{delegationError}</div>}
                {delegationWarning && <div className="warning-box">{delegationWarning}</div>}
              </div>
            )}
          </div>

          <h3>Partecipanti</h3>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
            {student.firstName} {student.lastName} è stato aggiunto come primo partecipante. Selezionare il menù.
            {!delegationActive && (
              <><br /><strong>Nota:</strong> Il bambino deve essere accompagnato da almeno un adulto (Genitore).</>
            )}
            {delegationActive && (
              <><br /><strong>Nota:</strong> Vostro figlio sarà accompagnato dal genitore delegato. Non è possibile aggiungere altri partecipanti.</>
            )}
          </p>
          <div className="participant-list">
            {participants.map((participant, index) => (
              <div key={index} className="participant-item">
                <div className="participant-header">
                  <strong>Partecipante {index + 1}</strong>
                  {index > 0 && <button className="btn-danger" onClick={() => removeParticipant(index)}>Rimuovi</button>}
                </div>
                <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr' }}>
                  <div className="form-group">
                    <label>Nome</label>
                    <input type="text" value={participant.firstName} disabled={index === 0}
                      onChange={(e) => updateParticipant(index, 'firstName', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Cognome</label>
                    <input type="text" value={participant.lastName} disabled={index === 0}
                      onChange={(e) => updateParticipant(index, 'lastName', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Relazione</label>
                    <select value={participant.relation} disabled={index === 0}
                      onChange={(e) => updateParticipant(index, 'relation', e.target.value)}>
                      {config.relations.map(rel => {
                        const genitoreExists = participants.some((p, i) => i !== index && p.relation === 'Genitore');
                        const disabled = rel === 'Genitore' && genitoreExists && participant.relation !== 'Genitore';
                        return <option key={rel} value={rel} disabled={disabled}>{rel}{disabled ? ' (già presente)' : ''}</option>;
                      })}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Menù</label>
                    <select value={participant.menu}
                      onChange={(e) => updateParticipant(index, 'menu', e.target.value)}>
                      {config.menus.map(menu => (
                        <option key={menu.name} value={menu.name}>{menu.name} (€{menu.price})</option>
                      ))}
                    </select>
                  </div>
                </div>
                {participant.relation === 'Fratello/Sorella' && index > 0 && (
                  <div className="sibling-student-section">
                    <div className="checkbox-group">
                      <label className="checkbox-label">
                        <input type="checkbox" checked={participant.siblingStudent || false}
                          onChange={(e) => updateParticipant(index, 'siblingStudent', e.target.checked)} />
                        Questo fratello/sorella è uno studente della scuola
                      </label>
                    </div>
                    {participant.siblingStudent && (
                      <div className="form-group" style={{ marginTop: '10px', maxWidth: '200px' }}>
                        <label>Classe del fratello/sorella</label>
                        <select value={participant.siblingClass || ''}
                          onChange={(e) => updateParticipant(index, 'siblingClass', e.target.value)}>
                          <option value="">Seleziona classe</option>
                          {(config.classes || ['1A','1B','1C','2A','2B','2C','3A','3B','3C']).map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    )}
                    <p style={{ fontSize: '12px', color: '#888', marginTop: '5px' }}>
                      Solo per fratelli/sorelle. Per aggiungere un amico, utilizzare l'opzione delega.
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <button className="btn-secondary" onClick={addParticipant} style={{ marginRight: '10px', display: delegationActive ? 'none' : 'inline-block' }}>
            Aggiungi Partecipante
          </button>

          <div style={{ marginTop: '15px', fontSize: '18px', fontWeight: 'bold' }}>
            Totale: €{participants.reduce((sum, p) => {
              const menuObj = config.menus.find(m => m.name === p.menu);
              return sum + (menuObj ? menuObj.price : 0);
            }, 0)}
          </div>

          {error && <div className="error">{error}</div>}
          {success && <div className="success">{success}</div>}

          <div style={{ marginTop: '20px' }}>
            <button className="btn-secondary" onClick={() => setStep(1)} style={{ marginRight: '10px' }}>Indietro</button>
            <button className="btn-primary" onClick={() => { setError(''); goToStep3(); }}>Avanti</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div>
          <h2>Passo 3: Firma e Conferma</h2>

          {/* Card A: Regolamento */}
          <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
            <h3 style={{ marginTop: 0 }}>📋 Regolamento</h3>
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input type="checkbox" checked={rulebookAccepted}
                  onChange={(e) => setRulebookAccepted(e.target.checked)} />
                Ho letto e accetto il <a href={`${API_BASE}/rulebook`} target="_blank" rel="noopener noreferrer" className="rulebook-link">regolamento della festa</a>
              </label>
            </div>
          </div>

          {/* Card B: Liberatoria */}
          <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
            <h3 style={{ marginTop: 0 }}>📝 Liberatoria (Dichiarazione di Vigilanza)</h3>

            <div style={{ marginBottom: '15px' }}>
              <p style={{ fontSize: '14px', color: '#555', marginBottom: '10px' }}>Scegliere la modalità di firma:</p>
              <div style={{ display: 'flex', gap: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input type="radio" name="liberatoriaMode" value="paper" checked={liberatoriaMode === 'paper'}
                    onChange={() => setLiberatoriaMode('paper')} />
                  <span>📄 Firma cartacea</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input type="radio" name="liberatoriaMode" value="electronic" checked={liberatoriaMode === 'electronic'}
                    onChange={() => setLiberatoriaMode('electronic')} />
                  <span>✍️ Firma elettronica (OTP)</span>
                </label>
              </div>
              {liberatoriaMode === 'paper' && (
                <p style={{ fontSize: '13px', color: '#888', marginTop: '8px' }}>
                  Il PDF sarà da stampare, firmare a mano e consegnare al rappresentante di classe.
                </p>
              )}
              {liberatoriaMode === 'electronic' && (
                <p style={{ fontSize: '13px', color: '#888', marginTop: '8px' }}>
                  La firma avverrà tramite codice OTP inviato alla tua email. Nessuna stampa necessaria.
                </p>
              )}
            </div>

            <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '8px', marginBottom: '15px' }}>
              <strong>In qualità di genitori/tutori di:</strong> {student.firstName} {student.lastName} (Classe {student.class})
            </div>

            <h4>Primo Genitore {liberatoriaMode === 'electronic' ? '(tutti i campi obbligatori)' : '(opzionale — compilabile su carta)'}</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Nome {liberatoriaMode === 'electronic' && '*'}</label>
                <input type="text" value={liberatoria.parent1.firstName}
                  onChange={(e) => setLiberatoria({ ...liberatoria, parent1: { ...liberatoria.parent1, firstName: e.target.value } })} />
              </div>
              <div className="form-group">
                <label>Cognome {liberatoriaMode === 'electronic' && '*'}</label>
                <input type="text" value={liberatoria.parent1.lastName}
                  onChange={(e) => setLiberatoria({ ...liberatoria, parent1: { ...liberatoria.parent1, lastName: e.target.value } })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Luogo di nascita {liberatoriaMode === 'electronic' && '*'}</label>
                <input type="text" value={liberatoria.parent1.birthPlace}
                  onChange={(e) => setLiberatoria({ ...liberatoria, parent1: { ...liberatoria.parent1, birthPlace: e.target.value } })} />
              </div>
              <div className="form-group" style={{ maxWidth: '100px' }}>
                <label>Prov. {liberatoriaMode === 'electronic' && '*'}</label>
                <input type="text" maxLength={2} value={liberatoria.parent1.birthProvince}
                  onChange={(e) => setLiberatoria({ ...liberatoria, parent1: { ...liberatoria.parent1, birthProvince: e.target.value.toUpperCase() } })} />
              </div>
              <div className="form-group">
                <label>Data di nascita {liberatoriaMode === 'electronic' && '*'}</label>
                <input type="text" placeholder="GG/MM/AAAA" value={liberatoria.parent1.birthDate} maxLength={10}
                  onChange={(e) => setLiberatoria({ ...liberatoria, parent1: { ...liberatoria.parent1, birthDate: formatDateInput(e.target.value, liberatoria.parent1.birthDate) } })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group" style={{ flex: 2 }}>
                <label>Indirizzo (via e numero) {liberatoriaMode === 'electronic' && '*'}</label>
                <input type="text" value={liberatoria.parent1.address}
                  onChange={(e) => setLiberatoria({ ...liberatoria, parent1: { ...liberatoria.parent1, address: e.target.value } })} />
              </div>
              <div className="form-group">
                <label>Città {liberatoriaMode === 'electronic' && '*'}</label>
                <input type="text" value={liberatoria.parent1.city}
                  onChange={(e) => setLiberatoria({ ...liberatoria, parent1: { ...liberatoria.parent1, city: e.target.value } })} />
              </div>
              <div className="form-group" style={{ maxWidth: '100px' }}>
                <label>Prov. {liberatoriaMode === 'electronic' && '*'}</label>
                <input type="text" maxLength={2} value={liberatoria.parent1.province}
                  onChange={(e) => setLiberatoria({ ...liberatoria, parent1: { ...liberatoria.parent1, province: e.target.value.toUpperCase() } })} />
              </div>
            </div>

            <h4 style={{ marginTop: '20px' }}>Secondo Genitore — opzionale</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Nome</label>
                <input type="text" value={liberatoria.parent2.firstName}
                  onChange={(e) => setLiberatoria({ ...liberatoria, parent2: { ...liberatoria.parent2, firstName: e.target.value } })} />
              </div>
              <div className="form-group">
                <label>Cognome</label>
                <input type="text" value={liberatoria.parent2.lastName}
                  onChange={(e) => setLiberatoria({ ...liberatoria, parent2: { ...liberatoria.parent2, lastName: e.target.value } })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Luogo di nascita</label>
                <input type="text" value={liberatoria.parent2.birthPlace}
                  onChange={(e) => setLiberatoria({ ...liberatoria, parent2: { ...liberatoria.parent2, birthPlace: e.target.value } })} />
              </div>
              <div className="form-group" style={{ maxWidth: '100px' }}>
                <label>Prov.</label>
                <input type="text" maxLength={2} value={liberatoria.parent2.birthProvince}
                  onChange={(e) => setLiberatoria({ ...liberatoria, parent2: { ...liberatoria.parent2, birthProvince: e.target.value.toUpperCase() } })} />
              </div>
              <div className="form-group">
                <label>Data di nascita</label>
                <input type="text" placeholder="GG/MM/AAAA" value={liberatoria.parent2.birthDate} maxLength={10}
                  onChange={(e) => setLiberatoria({ ...liberatoria, parent2: { ...liberatoria.parent2, birthDate: formatDateInput(e.target.value, liberatoria.parent2.birthDate) } })} />
              </div>
            </div>
            <div className="checkbox-group" style={{ marginBottom: '10px' }}>
              <label className="checkbox-label">
                <input type="checkbox" checked={liberatoria.parent2.sameAddress}
                  onChange={(e) => setLiberatoria({ ...liberatoria, parent2: { ...liberatoria.parent2, sameAddress: e.target.checked } })} />
                Stesso indirizzo del primo genitore
              </label>
            </div>
            {!liberatoria.parent2.sameAddress && (
              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label>Indirizzo (via e numero)</label>
                  <input type="text" value={liberatoria.parent2.address}
                    onChange={(e) => setLiberatoria({ ...liberatoria, parent2: { ...liberatoria.parent2, address: e.target.value } })} />
                </div>
                <div className="form-group">
                  <label>Città</label>
                  <input type="text" value={liberatoria.parent2.city}
                    onChange={(e) => setLiberatoria({ ...liberatoria, parent2: { ...liberatoria.parent2, city: e.target.value } })} />
                </div>
                <div className="form-group" style={{ maxWidth: '100px' }}>
                  <label>Prov.</label>
                  <input type="text" maxLength={2} value={liberatoria.parent2.province}
                    onChange={(e) => setLiberatoria({ ...liberatoria, parent2: { ...liberatoria.parent2, province: e.target.value.toUpperCase() } })} />
                </div>
              </div>
            )}

            {/* OTP Email field — only for electronic mode */}
            {liberatoriaMode === 'electronic' && (
              <div className="form-group" style={{ marginTop: '15px' }}>
                <label>📧 Email per ricevere il codice OTP *</label>
                <input type="email" value={otpEmail} placeholder="esempio@email.com"
                  onChange={(e) => setOtpEmail(e.target.value)} />
                <p style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                  Il codice di verifica verrà inviato a questo indirizzo.
                </p>
              </div>
            )}
          </div>

          {/* Card C: Delega (only if delegation active) */}
          {delegationActive && (
            <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
              <h3 style={{ marginTop: 0 }}>🤝 Delega</h3>
              <p style={{ fontSize: '14px', color: '#555', marginBottom: '10px' }}>
                Delega a <strong>{delegation.delegatedParent.firstName} {delegation.delegatedParent.lastName}</strong> per la vigilanza su {student.firstName} {student.lastName}.
              </p>

              <div style={{ marginBottom: '15px' }}>
                <p style={{ fontSize: '14px', color: '#555', marginBottom: '10px' }}>Scegliere la modalità di firma della delega:</p>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="radio" name="delegationMode" value="paper" checked={delegationMode === 'paper'}
                      onChange={() => setDelegationMode('paper')} />
                    <span>📄 Firma cartacea</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="radio" name="delegationMode" value="electronic" checked={delegationMode === 'electronic'}
                      onChange={() => setDelegationMode('electronic')} />
                    <span>✍️ Firma elettronica (OTP)</span>
                  </label>
                </div>
              </div>

              {delegationMode === 'electronic' && (
                <div>
                  <div className="form-group">
                    <label>📧 Email del genitore delegato *</label>
                    <input type="email" value={delegatedParentEmail} placeholder="email@genitore-delegato.com"
                      onChange={(e) => setDelegatedParentEmail(e.target.value)} />
                  </div>
                  <div style={{ background: '#e8f4fd', border: '1px solid #b8daff', padding: '12px', borderRadius: '6px', marginTop: '10px' }}>
                    <p style={{ fontSize: '13px', color: '#004085', margin: 0 }}>
                      ℹ️ Dopo la tua firma, invieremo un'email a <strong>{delegatedParentEmail || '[email]'}</strong> con un link per la controfirma del genitore delegato.
                    </p>
                  </div>
                </div>
              )}
              {delegationMode === 'paper' && (
                <p style={{ fontSize: '13px', color: '#888' }}>
                  Il PDF della delega sarà da stampare, firmare da entrambi i genitori e consegnare.
                </p>
              )}
            </div>
          )}

          {/* Summary */}
          {needsOtp() && (
            <div style={{ background: '#fff8e1', border: '1px solid #ffcc02', padding: '12px', borderRadius: '8px', marginBottom: '15px' }}>
              <p style={{ fontSize: '13px', color: '#856404', margin: 0 }}>
                ⚠️ Dopo aver cliccato "Invia Registrazione", riceverai un codice OTP a 6 cifre via email per confermare la firma elettronica. Non chiudere questa pagina.
              </p>
            </div>
          )}

          {error && <div className="error">{error}</div>}
          {success && <div className="success">{success}</div>}

          <div style={{ marginTop: '20px' }}>
            <button className="btn-secondary" onClick={() => { setError(''); setStep(2); }} style={{ marginRight: '10px' }}>Indietro</button>
            <button className="btn-primary" onClick={handleStep3Submit} disabled={submitting || otpSending}>
              {otpSending ? 'Invio codice...' : submitting ? 'Invio in corso...' : 'Invia Registrazione'}
            </button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div style={{ maxWidth: '450px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center' }}>🔐 Verifica Codice OTP</h2>
          <p style={{ textAlign: 'center', color: '#666', fontSize: '14px' }}>
            Un codice di verifica a 6 cifre è stato inviato a <strong>{otpMaskedEmail}</strong>
          </p>

          {/* Documents being signed */}
          <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>Documenti firmati con questo codice:</p>
            {liberatoriaMode === 'electronic' && (
              <p style={{ fontSize: '13px', margin: '4px 0' }}>✓ Liberatoria (Dichiarazione di Vigilanza)</p>
            )}
            {delegationActive && delegationMode === 'electronic' && (
              <p style={{ fontSize: '13px', margin: '4px 0' }}>✓ Delega (firma del genitore delegante)</p>
            )}
          </div>

          <div style={{ textAlign: 'center', margin: '20px 0' }}>
            <input
              type="text"
              maxLength={6}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              style={{ fontSize: '32px', textAlign: 'center', width: '200px', letterSpacing: '8px', padding: '10px', border: '2px solid #ddd', borderRadius: '8px' }}
              autoFocus
            />
          </div>

          {otpError && <div className="error" style={{ textAlign: 'center' }}>{otpError}</div>}

          <div style={{ textAlign: 'center', marginTop: '15px' }}>
            <button className="btn-primary" onClick={verifyOtp} disabled={otpVerifying || otpCode.length !== 6} style={{ width: '100%', maxWidth: '300px', padding: '14px' }}>
              {otpVerifying ? 'Verifica in corso...' : 'Conferma e Registra'}
            </button>
          </div>

          <div style={{ textAlign: 'center', marginTop: '15px', fontSize: '13px', color: '#888' }}>
            {otpCountdown > 0
              ? `Il codice scade tra ${formatOtpTime(otpCountdown)}`
              : 'Codice scaduto — richiedere un nuovo codice'}
          </div>

          <div style={{ textAlign: 'center', marginTop: '10px', display: 'flex', justifyContent: 'center', gap: '15px' }}>
            <button className="btn-secondary" onClick={resendOtp} disabled={otpResendCooldown > 0 || otpSending} style={{ fontSize: '13px' }}>
              {otpResendCooldown > 0 ? `Reinvia codice (${otpResendCooldown}s)` : '🔄 Reinvia codice'}
            </button>
            <button className="btn-secondary" onClick={() => {
              const newEmail = prompt('Inserire il nuovo indirizzo email:', otpEmail);
              if (newEmail && newEmail !== otpEmail) changeOtpEmail(newEmail);
            }} style={{ fontSize: '13px' }}>
              ✏️ Modifica email
            </button>
          </div>

          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <button className="btn-secondary" onClick={() => { setStep(3); setOtpError(''); }} style={{ fontSize: '13px' }}>
              ← Torna al passo precedente
            </button>
          </div>

          <p style={{ fontSize: '12px', color: '#888', marginTop: '20px', textAlign: 'center' }}>
            La conferma tramite codice OTP costituisce firma elettronica ai sensi del Reg. UE 910/2014 (eIDAS) e del D.Lgs. 82/2005 (CAD).
          </p>
        </div>
      )}
    </div>
  );
}

export default RegistrationForm;
